import { planFor, QUESTIONS, type Answers } from './questions';

/**
 * Drops the answers of questions that are no longer in the plan. Changing an earlier answer can take a later
 * question out of the plan (subscription yes -> no leaves `subscriptionType` behind; channels -> none leaves
 * the ranked other channels). Left in, an orphan would still count as a paid channel, still feed the research
 * and still reach the brand profile input.
 *
 * One pass in question order, asking each `when()` about the answers kept so far. A question's `when` only
 * reads earlier questions, so this is exact, and it breaks the loop that asking `planFor(all answers)` has:
 * with channels "none" the ranked other channels would still make the brand "run ads", keeping their own
 * question in the plan. Returns the same object when nothing is dropped.
 */
export function pruneOrphans(answers: Answers): Answers {
  const kept: Answers = {};
  let count = 0;
  for (const question of QUESTIONS) {
    const answer = answers[question.id];
    if (answer === undefined || (question.when && !question.when(kept))) continue;
    kept[question.id] = answer;
    count += 1;
  }
  return count === Object.keys(answers).length ? answers : kept;
}

/**
 * The question to show on Back: the one before `reviewId` in the plan, or, when no question is being reviewed,
 * the one before the first unanswered question. Null when there is nothing earlier (the first question) or the
 * quiz is already done, so Back is only ever offered while the quiz is open.
 */
export function previousQuestionId(answers: Answers, reviewId: string | null): string | null {
  const plan = planFor(answers);
  const reviewing = reviewId === null ? -1 : plan.findIndex((q) => q.id === reviewId);
  const at = reviewing >= 0 ? reviewing : plan.findIndex((q) => answers[q.id] === undefined);
  if (at < 0) return null;
  return at > 0 ? plan[at - 1]!.id : reviewing >= 0 ? reviewId : null;
}
